// src/pages/Contact.jsx
import React, { useRef, useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2;

const MAX_MB = Number(process.env.REACT_APP_FORMS_MAX_FILE_MB || 10);
const MAX_BYTES = MAX_MB * 1024 * 1024;

const ACCEPTED_EXTS = [".pdf", ".txt", ".doc", ".docx", ".xls", ".xlsx", ".csv"];
const ACCEPTED_MIMES = [
  "application/pdf",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const ACCEPT_ATTR = ["image/*", ...ACCEPTED_EXTS].join(",");

function mimeAllowed(file) {
  if (!file) return false;
  if (file.type && (file.type.startsWith("image/") || ACCEPTED_MIMES.includes(file.type))) return true;
  const lower = file.name.toLowerCase();
  return (
    ACCEPTED_EXTS.some((ext) => lower.endsWith(ext)) ||
    /\.(jpg|jpeg|png|gif|webp|bmp|tiff)$/.test(lower)
  );
}

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [files, setFiles] = useState([]); // [{file, id, error}]
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);
  const recaptchaRef = useRef(null);
  const fileInputRef = useRef(null);

  // 📱 detect phone/tablet to match the app-wide 1024px breakpoint
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validateRequired = () => {
    const errs = {};
    const name = (form.name || "").trim();
    const email = (form.email || "").trim();
    const message = (form.message || "").trim();

    if (!name) errs.name = "Name is required.";
    if (!email) {
      errs.email = "Email is required.";
    } else {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!ok) errs.email = "Enter a valid email.";
    }
    if (!message) errs.message = "Message is required.";

    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setNotice({ type: "error", msg: "Please fix the highlighted fields." });
      return false;
    }
    return true;
  };

  const addFiles = (fileList) => {
    const next = [];
    for (const f of Array.from(fileList || [])) {
      if (!mimeAllowed(f)) {
        next.push({
          id: crypto.randomUUID(),
          file: f,
          error:
            "Unsupported file type. Allowed: images (JPG/PNG/GIF/WEBP/BMP/TIFF), PDF, TXT, DOC/DOCX, XLS/XLSX, CSV.",
        });
        continue;
      }
      if (f.size <= 0 || f.size > MAX_BYTES) {
        next.push({
          id: crypto.randomUUID(),
          file: f,
          error: `File too large. Maximum ${MAX_MB} MB each.`,
        });
        continue;
      }
      next.push({ id: crypto.randomUUID(), file: f, error: null });
    }
    setFiles((prev) => [...prev, ...next]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (id) => {
    if (submitting) return;
    setFiles((prev) => prev.filter((x) => x.id !== id));
  };

  // --- reCAPTCHA helpers (v2 Invisible) ---
  const getFreshToken = async () => {
    if (!recaptchaRef.current) throw new Error("captcha-not-ready");
    const EXEC_TIMEOUT_MS = 15000;

    const execPromise = recaptchaRef.current.executeAsync();
    const timeoutPromise = new Promise((_, rej) =>
      setTimeout(() => rej(new Error("captcha-timeout")), EXEC_TIMEOUT_MS)
    );

    try {
      const token = await Promise.race([execPromise, timeoutPromise]);
      return token;
    } finally {
      try { recaptchaRef.current.reset(); } catch {}
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setNotice(null);

    if (!SITE_KEY_V2) {
      setNotice({ type: "error", msg: "reCAPTCHA not configured." });
      return;
    }
    if (!BACKEND) {
      setNotice({ type: "error", msg: "Backend URL not configured." });
      return;
    }
    if (!validateRequired()) {
      return;
    }

    try {
      setSubmitting(true);

      // 1) Upload each staged file (only those without errors)
      const cleanFiles = files.filter((f) => !f.error);
      const attachments = [];

      for (const entry of cleanFiles) {
        const f = entry.file;

        // Fresh token for /forms/upload-url
        const uploadToken = await getFreshToken();

        // Request a short-lived upload URL
        const upRes = await fetch(`${BACKEND}/forms/upload-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
          body: JSON.stringify({
            name: f.name,
            size: f.size,
            contentType: f.type || "application/octet-stream",
            recaptchaToken: uploadToken,
          }),
        });

        const upData = await upRes.json().catch(() => ({}));
        if (!upRes.ok || !upData.ok || !upData.upload?.url) {
          const reason = upData?.reason || upData?.error || "upload-url-failed";
          throw new Error(`Upload URL error (${reason}) for ${f.name}`);
        }

        // PUT the bytes
        const putRes = await fetch(upData.upload.url, {
          method: "PUT",
          headers: { "Content-Type": f.type || "application/octet-stream" },
          body: f,
        });
        if (!putRes.ok) {
          throw new Error(`Upload failed (${putRes.status}) for ${f.name}`);
        }

        attachments.push({
          name: f.name,
          size: f.size,
          contentType: f.type || "application/octet-stream",
          storagePath: upData.upload.storagePath,
        });
      }

      // 2) Fresh token for final /forms/submit
      const submitToken = await getFreshToken();

      const res = await fetch(`${BACKEND}/forms/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({
          type: "contact",
          fullName: form.name,
          email: form.email,
          message: form.message,
          attachments,
          recaptchaToken: submitToken,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const reason = data?.reason || data?.error || "send-failed";
        throw new Error(reason);
      }

      // Success
      setNotice({ type: "ok", msg: "Thanks! Your message was sent." });
      setForm({ name: "", email: "", message: "" });
      setFieldErrors({});
      setFiles([]);
      fileInputRef.current && (fileInputRef.current.value = "");
    } catch (err) {
      const em = String(err?.message || "");
      const msg =
        em.includes("captcha") || em.includes("Timeout")
          ? "reCAPTCHA expired or failed. Please try again."
          : `Could not send your message (${em}).`;
      setNotice({ type: "error", msg });
    } finally {
      setSubmitting(false);
    }
  };

  const pageStyle = {
    ...styles.page,
    ...(isSmallScreen ? styles.pageMobile : null),
  };

  const titleStyle = {
    ...styles.title,
    ...(isSmallScreen ? styles.titleMobile : null),
  };

  const contentGridStyle = {
    ...styles.contentGrid,
    ...(isSmallScreen ? styles.contentGridMobile : null),
  };

  const sideStackStyle = {
    ...styles.sideStack,
    ...(isSmallScreen ? styles.sideStackMobile : null),
  };

  return (
    <div style={pageStyle}>
      <section style={styles.header}>
        <h1 style={titleStyle}>Contact Us</h1>
        <p style={styles.subtitle}>
          Questions, support requests, or feedback? Send us a message and our team will review it.
        </p>
      </section>

      <div style={contentGridStyle}>
        <aside style={sideStackStyle}>
          <InfoCard icon="☏" title="How we can help">
            <ul style={styles.helperList}>
              <li style={styles.helperItem}><span style={styles.checkIcon}>✓</span> General questions</li>
              <li style={styles.helperItem}><span style={styles.checkIcon}>✓</span> Account or billing help</li>
              <li style={styles.helperItem}><span style={styles.checkIcon}>✓</span> Technical support</li>
            </ul>
          </InfoCard>

          <InfoCard icon="✉" title="Response">
            <p style={styles.cardText}>
              We aim to review messages as quickly as possible.
            </p>
            <a style={styles.link} href="mailto:support@asklawly.com">
              support@asklawly.com
            </a>
          </InfoCard>

          <InfoCard icon="!" title="Privacy note" tone="warning">
            <p style={styles.cardText}>
              Do not include passwords, full payment card numbers, or unnecessary sensitive information.
            </p>
          </InfoCard>
        </aside>

        <main style={styles.formPanel}>
          <form onSubmit={handleSubmit} style={styles.form} noValidate>
            <div style={styles.inputWrap}>
              <label style={styles.label} htmlFor="contact-name">Your Name</label>
              <input
                id="contact-name"
                name="name"
                placeholder="Enter your full name"
                value={form.name}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  borderColor: fieldErrors.name ? "#B91C1C" : styles.inputBorderColor,
                }}
                required
                disabled={submitting}
                aria-invalid={!!fieldErrors.name}
              />
              {fieldErrors.name && <div style={styles.errorText}>{fieldErrors.name}</div>}
            </div>

            <div style={styles.inputWrap}>
              <label style={styles.label} htmlFor="contact-email">Your Email</label>
              <input
                id="contact-email"
                name="email"
                placeholder="Enter your email address"
                value={form.email}
                onChange={handleChange}
                style={{
                  ...styles.input,
                  borderColor: fieldErrors.email ? "#B91C1C" : styles.inputBorderColor,
                }}
                type="email"
                required
                disabled={submitting}
                aria-invalid={!!fieldErrors.email}
              />
              {fieldErrors.email && <div style={styles.errorText}>{fieldErrors.email}</div>}
            </div>

            <div style={styles.inputWrap}>
              <label style={styles.label} htmlFor="contact-message">Your Message</label>
              <textarea
                id="contact-message"
                name="message"
                placeholder="How can we help you?"
                value={form.message}
                onChange={handleChange}
                style={{
                  ...styles.textarea,
                  borderColor: fieldErrors.message ? "#B91C1C" : styles.inputBorderColor,
                }}
                required
                disabled={submitting}
                aria-invalid={!!fieldErrors.message}
              />
              {fieldErrors.message && <div style={styles.errorText}>{fieldErrors.message}</div>}
            </div>

            <div style={styles.uploadSection}>
              <div style={styles.uploadHeader}>
                <div>
                  <label style={styles.label}>Attach files</label>
                  <p style={styles.uploadHint}>
                    Images, PDF, TXT, DOC, XLS, and CSV — maximum {MAX_MB} MB each.
                  </p>
                </div>
              </div>

              <label
                style={{
                  ...styles.uploadBox,
                  ...(submitting ? styles.uploadBoxDisabled : null),
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_ATTR}
                  multiple
                  onChange={(e) => addFiles(e.target.files)}
                  disabled={submitting}
                  style={styles.hiddenFileInput}
                />
                <span style={styles.uploadIcon}>⇧</span>
                <span style={styles.uploadText}>Click to upload files</span>
                <span style={styles.uploadSubText}>
                  You may attach screenshots, documents, or spreadsheets.
                </span>
              </label>
            </div>

            {files.length > 0 && (
              <div style={styles.fileList}>
                {files.map((f) => (
                  <div key={f.id} style={styles.fileRow}>
                    <div style={styles.fileIcon}>{f.error ? "!" : "▣"}</div>
                    <div style={styles.fileMeta}>
                      <div style={styles.fileName}>{f.file.name}</div>
                      <div style={styles.fileSize}>
                        {Math.round(f.file.size / 1024)} KB
                        {f.error && <span style={styles.fileError}> • {f.error}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(f.id)}
                      style={styles.removeBtn}
                      disabled={submitting}
                      aria-label={`Remove ${f.file.name}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Invisible reCAPTCHA — executes programmatically */}
            <div style={{ height: 0, overflow: "hidden" }}>
              <ReCAPTCHA ref={recaptchaRef} sitekey={SITE_KEY_V2} size="invisible" />
            </div>

            <button type="submit" style={styles.button} disabled={submitting}>
              <span style={styles.buttonIcon}>✈</span>
              {submitting ? "Sending…" : "Send"}
            </button>

            {notice && (
              <div
                style={{
                  ...styles.notice,
                  ...(notice.type === "ok" ? styles.noticeOk : styles.noticeError),
                }}
              >
                <span style={styles.noticeIcon}>{notice.type === "ok" ? "✓" : "!"}</span>
                <span>{notice.msg}</span>
              </div>
            )}
          </form>
        </main>
      </div>
    </div>
  );
};

const InfoCard = ({ icon, title, tone, children }) => {
  const isWarning = tone === "warning";
  return (
    <section style={styles.infoCard}>
      <div
        style={{
          ...styles.infoIcon,
          ...(isWarning ? styles.infoIconWarning : null),
        }}
      >
        {icon}
      </div>
      <div style={styles.infoContent}>
        <h2 style={styles.infoTitle}>{title}</h2>
        {children}
      </div>
    </section>
  );
};

const styles = {
  page: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "18px 16px 44px",
    boxSizing: "border-box",
    background: "#F5F7FA",
    fontFamily: "sans-serif",
  },
  pageMobile: {
    padding: "14px 12px 34px",
  },

  header: {
    padding: "12px 0 18px",
  },
  title: {
    margin: 0,
    color: "#0F172A",
    fontSize: 34,
    lineHeight: 1.08,
    fontWeight: 950,
    letterSpacing: "-0.9px",
  },
  titleMobile: {
    fontSize: 30,
    textAlign: "center",
    letterSpacing: "-0.65px",
  },
  subtitle: {
    margin: "9px 0 0",
    maxWidth: 760,
    color: "#475569",
    fontSize: 15,
    lineHeight: 1.55,
    fontWeight: 650,
  },

  contentGrid: {
    display: "grid",
    gridTemplateColumns: "310px minmax(0, 1fr)",
    gap: 18,
    alignItems: "start",
  },
  contentGridMobile: {
    display: "block",
  },

  sideStack: {
    display: "grid",
    gap: 14,
  },
  sideStackMobile: {
    marginBottom: 14,
  },
  infoCard: {
    display: "grid",
    gridTemplateColumns: "58px minmax(0, 1fr)",
    gap: 14,
    alignItems: "start",
    padding: 18,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2,6,23,0.055)",
  },
  infoIcon: {
    width: 46,
    height: 46,
    borderRadius: 16,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    fontWeight: 950,
  },
  infoIconWarning: {
    background: "rgba(245,158,11,0.13)",
    color: "#D97706",
  },
  infoContent: {
    minWidth: 0,
  },
  infoTitle: {
    margin: "0 0 9px",
    color: "#0F172A",
    fontSize: 17,
    lineHeight: 1.25,
    fontWeight: 950,
    letterSpacing: "-0.25px",
  },
  cardText: {
    margin: "0 0 8px",
    color: "#334155",
    fontSize: 13.75,
    lineHeight: 1.55,
    fontWeight: 650,
  },
  helperList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "grid",
    gap: 8,
  },
  helperItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#334155",
    fontSize: 13.5,
    lineHeight: 1.4,
    fontWeight: 700,
  },
  checkIcon: {
    width: 18,
    height: 18,
    minWidth: 18,
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    fontSize: 11,
    fontWeight: 950,
  },

  formPanel: {
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 14px 34px rgba(2,6,23,0.065)",
    padding: 22,
    boxSizing: "border-box",
  },
  form: {
    display: "grid",
    gap: 15,
  },
  inputWrap: {
    width: "100%",
    display: "grid",
    gap: 7,
  },
  label: {
    display: "block",
    color: "#0F172A",
    fontSize: 13.5,
    lineHeight: 1.35,
    fontWeight: 950,
  },
  inputBorderColor: "rgba(2,6,23,0.16)",
  input: {
    padding: "12px 13px",
    fontSize: 15,
    border: "1px solid rgba(2,6,23,0.16)",
    borderRadius: 12,
    outline: "none",
    boxShadow: "0 8px 18px rgba(2,6,23,0.035)",
    width: "100%",
    boxSizing: "border-box",
    color: "#0F172A",
    background: "#FFFFFF",
    fontWeight: 650,
  },
  textarea: {
    padding: "12px 13px",
    fontSize: 15,
    border: "1px solid rgba(2,6,23,0.16)",
    borderRadius: 12,
    outline: "none",
    boxShadow: "0 8px 18px rgba(2,6,23,0.035)",
    width: "100%",
    minHeight: 150,
    resize: "vertical",
    boxSizing: "border-box",
    color: "#0F172A",
    background: "#FFFFFF",
    fontWeight: 650,
    lineHeight: 1.5,
  },
  errorText: {
    color: "#B91C1C",
    fontSize: 12.5,
    marginTop: 1,
    fontWeight: 800,
  },

  uploadSection: {
    display: "grid",
    gap: 8,
  },
  uploadHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  uploadHint: {
    margin: "4px 0 0",
    color: "#64748B",
    fontSize: 12.75,
    lineHeight: 1.4,
    fontWeight: 650,
  },
  uploadBox: {
    minHeight: 96,
    border: "1.5px dashed rgba(37,99,235,0.45)",
    background: "rgba(37,99,235,0.035)",
    borderRadius: 14,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    padding: 14,
    cursor: "pointer",
    textAlign: "center",
    boxSizing: "border-box",
  },
  uploadBoxDisabled: {
    opacity: 0.65,
    cursor: "default",
  },
  hiddenFileInput: {
    display: "none",
  },
  uploadIcon: {
    width: 30,
    height: 30,
    borderRadius: 999,
    background: "#FFFFFF",
    color: "#2563EB",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 950,
    boxShadow: "0 7px 16px rgba(37,99,235,0.12)",
  },
  uploadText: {
    color: "#0F172A",
    fontSize: 13.5,
    fontWeight: 900,
  },
  uploadSubText: {
    color: "#64748B",
    fontSize: 12.5,
    lineHeight: 1.35,
    fontWeight: 650,
  },

  fileList: {
    display: "grid",
    gap: 8,
    width: "100%",
    boxSizing: "border-box",
  },
  fileRow: {
    display: "grid",
    gridTemplateColumns: "34px minmax(0, 1fr) 36px",
    alignItems: "center",
    gap: 10,
    padding: "10px 10px",
    border: "1px solid rgba(2,6,23,0.10)",
    borderRadius: 12,
    background: "#F8FAFC",
  },
  fileIcon: {
    width: 30,
    height: 30,
    borderRadius: 10,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
    fontSize: 14,
  },
  fileMeta: {
    minWidth: 0,
  },
  fileName: {
    color: "#0F172A",
    fontSize: 13.25,
    fontWeight: 900,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileSize: {
    marginTop: 2,
    color: "#64748B",
    fontSize: 12.25,
    lineHeight: 1.35,
    fontWeight: 650,
  },
  fileError: {
    color: "#B91C1C",
    fontWeight: 800,
  },
  removeBtn: {
    width: 32,
    height: 32,
    borderRadius: 10,
    border: "1px solid rgba(185,28,28,0.22)",
    background: "#FFFFFF",
    color: "#B91C1C",
    cursor: "pointer",
    fontSize: 18,
    fontWeight: 950,
    lineHeight: 1,
  },

  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "13px 18px",
    fontSize: 15,
    backgroundColor: "#2563EB",
    color: "#FFFFFF",
    border: "1px solid rgba(37,99,235,0.22)",
    borderRadius: 12,
    cursor: "pointer",
    width: "100%",
    fontWeight: 950,
    boxShadow: "0 12px 24px rgba(37,99,235,0.20)",
  },
  buttonIcon: {
    fontSize: 15,
    fontWeight: 950,
  },

  notice: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 0,
    padding: "11px 12px",
    borderRadius: 13,
    fontSize: 13.5,
    lineHeight: 1.45,
    fontWeight: 800,
    textAlign: "center",
  },
  noticeOk: {
    color: "#047857",
    background: "#ECFDF5",
    border: "1px solid rgba(4,120,87,0.18)",
  },
  noticeError: {
    color: "#B91C1C",
    background: "#FEF2F2",
    border: "1px solid rgba(185,28,28,0.18)",
  },
  noticeIcon: {
    fontWeight: 950,
  },
  link: {
    color: "#2563EB",
    fontWeight: 850,
    textDecoration: "none",
    fontSize: 13.75,
  },
};

export default Contact;
